import React, { useState, useRef, useEffect } from 'react';
import {
	Badge,
	Box,
	Button,
	IconButton,
	Popover,
	SvgIcon,
	TextField,
	Tooltip,
	Typography,
	makeStyles
} from '@material-ui/core';
import { Settings as SettingsIcon } from 'react-feather';
import useSettings from 'src/hooks/useSettings';
import { LANGUAGES, THEMES } from 'src/submodules/commons-misc/constant';
import tenantsService from 'src/services/tenantService';
import { useTranslation } from 'react-i18next';
import { TRANSLATE } from 'src/submodules/commons-misc/constant';
import { useHistory, useLocation } from 'react-router';
import fleetService from './Fleet/fleetService';
import Language from './Language';
import layoutService from 'src/services/layoutService';
import { getTimeZoneList, saveOffset } from 'src/utils/common';
import useIsMountedRef from 'src/hooks/useIsMountedRef';
import Cookies from 'js-cookie';

const useStyles = makeStyles(theme => ({
	badge: {
		height: 10,
		width: 10,
		borderRadius: 5,
		marginTop: 10,
		marginRight: 5
	},
	popover: {
		width: 320,
		padding: theme.spacing(2)
	}
}));

function Settings() {
	const classes = useStyles();
	const ref = useRef(null);
	const { settings, saveSettings } = useSettings();
	const [isOpen, setOpen] = useState(false);
	const themeOptions = getThemes();
	const { t, i18n } = useTranslation();
	const history = useHistory();
	const isMobile = layoutService.isMobile();
	const isMountedRef = useIsMountedRef();
	const settingsFromCookies = Cookies.get('settings');
	const keyFromStorage = localStorage.getItem('i18nextLng');
	const [values, setValues] = useState({
		direction: settings.direction,
		responsiveFontSizes: settings.responsiveFontSizes,
		theme: settingsFromCookies
			? JSON.parse(settingsFromCookies)?.theme
			: settings.theme,
		language: settingsFromCookies
			? JSON.parse(settingsFromCookies)?.language
			: LANGUAGES[keyFromStorage]?.name,
		timezone: settings.timezone,
		fleet: settings.fleet
	});
	const tenants = tenantsService.getTenants();
	const [tenant, setTenant] = useState(
		tenantsService.getCurrentTenant()?.id || tenants?.[0]?.id
	);

	function getThemes() {
		let result = {};

		Object.keys(THEMES).map(key => {
			let theme = THEMES[key];
			let tntTheme = tenantsService.getCurrentTenant()?.properties?.props
				?.thema;

			if (theme?.parent) {
				if (tntTheme && tntTheme === theme?.name) {
					delete result[theme.parent];

					result[key] = theme;
				}
			} else {
				result[key] = theme;
			}
		});

		return result;
	}
	const handleOpen = () => {
		setOpen(true);
	};

	const handleClose = () => {
		setOpen(false);
	};

	const changeTimezone = timezone => {
		const timeZoneList = getTimeZoneList();
		var offsetTimezone = timeZoneList.filter(t => t.id === parseInt(timezone));
		saveOffset(offsetTimezone[0]);
	};

	const changeLanguage = lng => {
		i18n.changeLanguage(typeof lng === 'string' ? lng : lng?.name);
	};

	useEffect(() => {
		if (isMountedRef.current && isMobile) {
			if (settingsFromCookies) {
				i18n.changeLanguage(JSON.parse(settingsFromCookies)?.language);
			}
		}
	}, [isMountedRef]);

	const handleChange = (field, value) => {
		setValues({
			...values,
			[field]: value
		});
	};

	const handleSave = () => {
		saveSettings(values);
		if (isMobile) {
			changeTimezone(values.timezone);
			changeLanguage(values.language);
		}
		setOpen(false);
		let t = tenants.find(x => x.id == tenant);
		fleetService.clearStorage();
		history.push('/app');
		tenantsService.setCurrentTenant(t);
	};

	return (
		<>
			<Tooltip title={t(TRANSLATE.SETTINGS)}>
				<Badge color="secondary" classes={{ badge: classes.badge }}>
					<IconButton color="inherit" onClick={handleOpen} ref={ref}>
						<SvgIcon fontSize="small">
							<SettingsIcon />
						</SvgIcon>
					</IconButton>
				</Badge>
			</Tooltip>
			<Popover
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'center'
				}}
				classes={{ paper: classes.popover }}
				anchorEl={ref.current}
				onClose={handleClose}
				open={isOpen}
			>
				<Typography variant="h4" color="textPrimary">
					{t(TRANSLATE.SETTINGS)}
				</Typography>
				{/* THEME */}
				<Box mt={2}>
					<TextField
						fullWidth
						label={t(TRANSLATE.THEME)}
						name="theme"
						onChange={event => handleChange('theme', event.target.value)}
						select
						SelectProps={{ native: true }}
						value={values.theme}
						variant="outlined"
					>
						{Object.keys(themeOptions).map(key => {
							let theme = THEMES[key];

							return (
								<option key={theme?.name} value={theme?.name}>
									{t(theme?.label)}
								</option>
							);
						})}
					</TextField>
				</Box>

				{/* TENANTS */}
				<Box mt={2}>
					<TextField
						fullWidth
						label="Tenant"
						name="tenant"
						onChange={event => setTenant(event.target.value)}
						select
						SelectProps={{ native: true }}
						value={tenant}
						variant="outlined"
					>
						{tenants?.map(t => (
							<option key={t.id} value={t.id}>
								{t.name}
							</option>
						))}
					</TextField>
				</Box>
				{isMobile && (
					<>
						<Typography variant="h4" color="textPrimary">
							{t(TRANSLATE.LANGUAGE)}
						</Typography>
						<Box mt={2}>
							<TextField
								fullWidth
								label={t(TRANSLATE.LANGUAGE)}
								name="language"
								onChange={event => handleChange('language', event.target.value)}
								select
								SelectProps={{ native: true }}
								value={values.language}
								variant="outlined"
							>
								{Object.keys(LANGUAGES).map(key => {
									let language = LANGUAGES[key];

									return (
										<option key={language?.name} value={language?.name}>
											{t(language?.label)}
										</option>
									);
								})}
							</TextField>
						</Box>
						<Typography variant="h4" color="textPrimary">
							{'Timezone'}
						</Typography>
						<Box mt={2}>
							<TextField
								fullWidth
								label={'Timezone'}
								name="language"
								onChange={event => handleChange('timezone', event.target.value)}
								select
								SelectProps={{ native: true }}
								value={values.timezone}
								variant="outlined"
							>
								{getTimeZoneList().map(timezone => {
									return (
										<option key={timezone?.id} value={timezone?.id}>
											{timezone?.label}
										</option>
									);
								})}
							</TextField>
						</Box>
					</>
				)}
				<Box mt={2}>
					<Button
						variant="contained"
						color="secondary"
						fullWidth
						onClick={handleSave}
					>
						{t(TRANSLATE.SAVE)}
					</Button>
				</Box>
			</Popover>
		</>
	);
}

export default Settings;
